// ─────────────────────────────────────────────────────────────────────────────
// agentTransport.ts — WHICH LANE CARRIES THIS AGENT'S CALL.
//
// Owner ruling: there are two voice lanes and every agent reaches both from its
// own chat screen. LOCAL TTS is the existing pipeline — the one the CornerOrb has
// always used and always will. PHONE NUMBER places the call through a real number
// hooked into Rexy OS. The rule is THE AGENT'S CHAT, not the domain: an agent chat
// in BUSINESS behaves exactly as an agent chat in TRADING.
//
// Inbound is not this file's business and not this surface's: someone dialling the
// Rexy OS number never touches the app. That lane belongs to Sherman.
//
// ── THIS CLIENT OWNS THE SWITCH AND NOTHING BEHIND IT ──────────────────────
// It does not place calls, does not know how either lane is carried, and holds no
// provider name anywhere — not in a string, not in a comment, not in an
// identifier. The lanes are `local_tts` and `phone_number`. That is the whole
// vocabulary, and it is the vocabulary the owner ruled.
//
// ── NOTHING PUBLISHES THIS YET, AND THE SURFACE SAYS SO ────────────────────
// PROVISIONAL — THE ONE LINE THAT MOVES WHEN THE ROUTE LANDS.
// Probed live 2026-08-29 against the running Flask on :5000, both on the same
// host in the same second:
//
//     /api/rexy/agent-loop?domain=TRADING&agent=arthur   -> 401 Unauthorised
//     /api/rexy/agent-transport?domain=TRADING&agent=arthur -> 404
//
// The 401 is a route that exists and wants a token. The 404 is NO ROUTE AT ALL.
// That pair is the discriminator this file leans on, and it was measured rather
// than assumed. A toggle that implied a working phone path before one existed
// would be the `VOICE NOT PUBLISHED` fault in a new place, so it is not built:
// capability is READ from the route, never derived here and never defaulted to
// something that looks like a choice.
//
// ── 404 AND 500 ARE DIFFERENT FACTS AND MUST NOT RENDER ALIKE ──────────────
// "No route declares a phone lane" and "the route is there and we could not read
// it" are two different sentences, and collapsing them would repeat exactly the
// mistake `agentVoice.voiceForAgent` names: an empty set is not evidence that an
// agent has no voice, it is evidence we read nothing. So `published` is a
// THREE-valued fact — false / unknown / true — and the surface says which.
//
// If the route later lands BEHIND AUTH it will answer 401 like its sibling, and
// this client will report `published: 'unknown'`. That is correct: at that moment
// we genuinely have not read it.
//
// ── FAIL CLOSED TOWARD LOCAL TTS, WITH NO COERCION ─────────────────────────
// Same line `core/loop_toggle.py` holds and for the same reason: a value we did
// not write is a value we do not trust to mean consent. Only the exact string
// `phone_number` selects the phone lane. Missing key, unreadable body, unknown
// capability, `"true"`, `1`, `"yes"` — every one of them is LOCAL TTS, which is
// the lane that is actually wired and therefore the safe direction to fail in.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBase, authToken } from './apiBase';

/**
 * PROVISIONAL — the wire contract this surface is built against. Sherman owns
 * the phone lane and therefore owns this route; the shape below mirrors
 * `/rexy/agent-loop` deliberately, because that shape is already owner-ruled and
 * settled and a second vocabulary for "which per-agent setting is this" would be
 * the two-copies fault again.
 *
 *   GET  /api/rexy/agent-transport?domain=<D>&agent=<a>
 *   POST /api/rexy/agent-transport  { domain, agent, transport }
 *   ->   { domain, agent, transport, has_setting, capability }
 *
 * Per the loop precedent the stored state is keyed `"DOMAIN:agent"` in
 * memory/rexy_settings.json and defaults off — off meaning LOCAL TTS. THAT STATE
 * IS BACKEND-OWNED. This client keeps no copy of it: a selection held in React
 * would survive a reload looking like a saved setting and would be a lie about
 * persistence, which is the same class of fault as a toggle implying a lane.
 */
export const AGENT_TRANSPORT_PATH = '/rexy/agent-transport';

/** The two lanes. There is no third, and neither is ever named by its carrier. */
export type TransportLane = 'local_tts' | 'phone_number';

export const LOCAL_TTS: TransportLane = 'local_tts';
export const PHONE_NUMBER: TransportLane = 'phone_number';

/** A list, never a keyed object — `MAP[wireValue]` walks the prototype chain and
 *  "toString" comes back a truthy function, which has reached a render in this
 *  codebase once already. `indexOf` cannot. */
const LANES: readonly TransportLane[] = [LOCAL_TTS, PHONE_NUMBER];

/**
 * What the lanes can do for THIS agent. Backend-computed, never derived here —
 * the same rule `agentLoop.ts` holds: a copy of the decision in TypeScript would
 * be a second definition of the one thing the control exists to state truthfully.
 */
export type TransportCapability =
  /** Both lanes are reachable for this agent. The toggle may be moved. */
  | 'both'
  /** Only the local pipeline is reachable for this agent. */
  | 'local_only'
  /** We could not read what the lanes can do. NOT a state the backend reports. */
  | 'unknown';

const CAPABILITIES: readonly TransportCapability[] = ['both', 'local_only'];

/**
 * Does a route publish transport capability at all?
 *   false    — nothing does. Measured: the path answers 404 while its sibling 401s.
 *   'unknown'— something answered, and it was not an answer we could read.
 *   true     — the route answered and we read it.
 */
export type TransportPublished = true | false | 'unknown';

export interface AgentTransportState {
  domain: string;
  agent: string;
  /** The lane a call from this chat would take. Fails closed to LOCAL TTS. */
  transport: TransportLane;
  /** Whether a setting exists AT ALL for this agent. False ⇒ nobody has ruled on
   *  it, which is NOT the same as somebody ruling it to local. */
  hasSetting: boolean;
  capability: TransportCapability;
  published: TransportPublished;
  /** WHY the read failed, when it did. Diagnostics — see TransportUnreadReason.
   *  Null on every successful read, and never part of a sentence. */
  unreadReason?: TransportUnreadReason;
}

/** The state before anything is read: local lane, nothing ruled, nothing known. */
/**
 * WHICH WAY THE READ FAILED — diagnostics, never rendered.
 *
 * `published: 'unknown'` is reached by THREE different client paths and the
 * sentence the owner sees cannot tell them apart. It does not need to — the
 * sentence is about what is TRUE for him — but the person fixing the route does
 * need to, and this is the half only the client can see:
 *
 *   'network'    `fetch` itself threw. Refused, CORS-blocked, DNS. No status.
 *   'http_<n>'   the route answered <n>, and <n> is not 404. 404 is the ONE
 *                status read as "nothing publishes this" and it never lands
 *                here — it produces the NOT PUBLISHED sentence instead.
 *   'bad_json'   a 2xx whose body would not parse as JSON.
 *
 * NOT among them: an identity mismatch or a non-object body. Those return
 * `published: true` with `capability: 'unknown'`, which is a DIFFERENT sentence
 * ("THE ROUTE ANSWERED BUT DID NOT SAY WHICH LANES ARE REACHABLE"). So seeing
 * the COULD-NOT-READ sentence already rules that class out.
 *
 * It reaches the DOM as `data-transport-unread` on the chip — inspectable in
 * devtools, capturable by a screenshot's DOM dump, assertable by a bar, and
 * invisible to the owner. Nothing is added to any sentence.
 */
export type TransportUnreadReason = string | null;

export function unreadTransport(
  domain: string, agent: string, published: TransportPublished = 'unknown',
  unreadReason: TransportUnreadReason = null,
): AgentTransportState {
  return {
    domain, agent,
    transport: LOCAL_TTS,
    hasSetting: false,
    capability: 'unknown',
    published,
    unreadReason,
  };
}

/**
 * The body's OWN value for `key`, as a trimmed non-empty string, or undefined.
 * Never throws, and never walks the prototype chain.
 *
 * ADDED AFTER THE BAR CONVICTED THE FIRST VERSION, AND THE HONEST SIZE OF THAT
 * CATCH IS SMALL: `d` here comes from `res.json()`, and `JSON.parse` gives every
 * object a plain `Object.prototype` with no inherited `transport`, so the fault
 * was NOT PROVEN REACHABLE OVER THE WIRE. What it was is a module reading an
 * untrusted body with a bare `d.transport` — the shape that has already put a
 * truthy `toString` function on a render in this codebase once. The rule is
 * own-property lookup on anything the wire wrote; this now follows it, and all
 * four members of the class moved together rather than the one the bar named.
 */
function ownString(d: any, key: string): string | undefined {
  if (d === null || typeof d !== 'object') return undefined;
  if (!Object.prototype.hasOwnProperty.call(d, key)) return undefined;
  const v = (d as Record<string, unknown>)[key];
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

// apiBase and authToken are SHARED (src/api/apiBase.ts). This file used to
// carry its own copy of both, as five sibling clients did — six identical
// hardcodings of a host, and a built bundle pointed every one of them at
// herexy.com wherever it was served from. An instance fix leaves twins, so the
// answer moved to one module rather than being corrected here.


/**
 * The wire body → the state the surface renders. NEVER throws.
 *
 * `requested` is the identity the caller asked about, and the check against it is
 * not decoration: a body describing another agent is DISCARDED down to the unread
 * state, because painting it would be the exact cross-agent bleed a per-agent
 * setting exists to prevent. Turning the phone lane on for BUCK must not touch
 * DELTA, and the way a UI breaks that rule is not by writing the wrong key — it
 * is by painting a reply about one agent onto another after the user has moved.
 */
export function normalizeAgentTransport(
  d: any,
  requested: { domain: string; agent: string },
): AgentTransportState {
  const base = unreadTransport(requested.domain, requested.agent, true);
  if (!d || typeof d !== 'object') return base;

  // Identity gate. Case-insensitive because the backend keys agents lowercase and
  // domains uppercase, and casing is not a different agent. A MISSING field is
  // not a match — an unlabelled body could be about anyone.
  const sameAgent = ownString(d, 'agent')?.toLowerCase() === requested.agent.trim().toLowerCase();
  const sameDomain = ownString(d, 'domain')?.toUpperCase() === requested.domain.trim().toUpperCase();
  if (!sameAgent || !sameDomain) return base;

  const cap = ownString(d, 'capability');
  const capability: TransportCapability = cap !== undefined
    && CAPABILITIES.indexOf(cap as TransportCapability) >= 0
    ? (cap as TransportCapability)
    : 'unknown';

  // NO COERCION. Only the exact lane strings count; everything else is local.
  const lane = ownString(d, 'transport');
  const wire: TransportLane = lane !== undefined && LANES.indexOf(lane as TransportLane) >= 0
    ? (lane as TransportLane)
    : LOCAL_TTS;

  return {
    domain: requested.domain,
    agent: requested.agent,
    // A capability we could not read cannot be reported as on the phone lane: we
    // do not know that the lane is there. Local is the direction this fails in.
    transport: capability === 'unknown' ? LOCAL_TTS : wire,
    hasSetting: Object.prototype.hasOwnProperty.call(d, 'has_setting') && d.has_setting === true,
    capability,
    published: true,
    unreadReason: null,
  };
}

async function transportFetch(
  requested: { domain: string; agent: string },
  init: RequestInit,
  query = '',
): Promise<AgentTransportState> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${AGENT_TRANSPORT_PATH}${query}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken()}`,
        ...(init.headers || {}),
      },
    });
  } catch {
    // The network refused. That is not "no phone lane" — it is no answer.
    return unreadTransport(requested.domain, requested.agent, 'unknown', 'network');
  }
  // THE ONE PLACE THE THREE-VALUED FACT IS DECIDED. 404 is the only status read
  // as "nothing publishes this"; every other failure is an unread route.
  if (res.status === 404) {
    return unreadTransport(requested.domain, requested.agent, false);
  }
  if (!res.ok) {
    return unreadTransport(requested.domain, requested.agent, 'unknown',
                           `http_${res.status}`);
  }
  try {
    return normalizeAgentTransport(await res.json(), requested);
  } catch {
    return unreadTransport(requested.domain, requested.agent, 'unknown', 'bad_json');
  }
}

/** GET the live per-agent transport state. Never rejects — an unread route is a
 *  state the surface must render, not an exception it must catch. */
export function getAgentTransport(domain: string, agent: string): Promise<AgentTransportState> {
  const q = `?domain=${encodeURIComponent(domain)}&agent=${encodeURIComponent(agent)}`;
  return transportFetch({ domain, agent }, { method: 'GET' }, q);
}

/** POST the per-agent lane. Returns the EFFECTIVE state the backend now holds —
 *  never the value that was requested. */
export function setAgentTransport(
  domain: string, agent: string, transport: TransportLane,
): Promise<AgentTransportState> {
  return transportFetch({ domain, agent }, {
    method: 'POST',
    body: JSON.stringify({ domain, agent, transport }),
  });
}
